/**
 * Compose — the unified spread editor that replaced the Storyboard /
 * Illustrate / Review stages. One canvas-first workspace ("Stage"
 * direction from the design prototype): a hero two-page spread, a slim
 * tool dock (Select / Images / Text / Layers), a floating image bank with
 * the moment-driven generate flow, and a whole-book pager.
 */
import { useEffect, useRef, useState } from 'react';
import { BOOK_FONTS, ensureBookFontsRegistered } from '../../utils/bookFonts';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useComposeStage } from '../../hooks/useComposeStage';
import type { useProject } from '../../hooks/useProject';
import { SpreadCanvas } from '../compose/SpreadCanvas';
import { BankPanel } from '../compose/BankPanel';
import { LayersPanel } from '../compose/LayersPanel';
import { SpreadNavigator } from '../compose/SpreadNavigator';
import { PreviewOverlay } from '../compose/PreviewOverlay';
import { SplitStoryDialog } from './SplitStoryDialog';
import { UnreviewedElementsModal } from './UnreviewedElementsModal';
import { CastEditorModal } from './CastEditorModal';
import { RefineImageModal } from './refine/RefineImageModal';
import { StoryRail } from '../compose/StoryRail';
import {
  IconPointer,
  IconImage,
  IconType,
  IconLayers,
  IconArrowLeft,
  IconArrowRight,
  IconTrash,
  IconMagnifier,
  IconDownload,
  IconSparkle,
  IconWand,
} from '../compose/icons';

type ProjectHookReturn = ReturnType<typeof useProject>;

interface ComposeStageProps {
  projectHook: ProjectHookReturn;
}

type DockTool = 'bank' | 'layers' | null;

export function ComposeStage({ projectHook }: ComposeStageProps) {
  // The canvas renders text in the project's book font — make sure the
  // @font-face rules exist even when the author lands here directly
  // (refresh/deep link). Without them the browser falls back to a generic
  // family and line breaks stop matching the exported PDF.
  ensureBookFontsRegistered();

  const store = useComposeStage(projectHook);
  const {
    project,
    refetch,
    spreads,
    current,
    idx,
    labelFor,
    saveState,
    selectedEl,
    addText,
    removeEl,
    step,
    addSpread,
    deleteSpread,
    moveSpread,
    splitting,
    splitStory,
    unreviewedElements,
    layouting,
    layoutSpread,
    layoutUndo,
    undoLayout,
    goToStage,
    updateStoryElement,
  } = store;

  const [tool, setTool] = useState<DockTool>('bank');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [unreviewedModalOpen, setUnreviewedModalOpen] = useState(false);
  const [confirmDeleteSpread, setConfirmDeleteSpread] = useState(false);
  const [deletingSpread, setDeletingSpread] = useState(false);
  const [castEditorElementId, setCastEditorElementId] = useState<string | null>(null);
  const [refiningBank, setRefiningBank] = useState<{
    id: string;
    r2Key: string;
    caption: string;
  } | null>(null);

  // Delete/Backspace removes the selected element (unless typing).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const t = document.activeElement?.tagName;
      if (t === 'TEXTAREA' || t === 'INPUT') return;
      if (selectedEl) {
        e.preventDefault();
        removeEl(selectedEl);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedEl, removeEl]);

  if (!project) return null;

  const bookFont = {
    family:
      BOOK_FONTS.find((f) => f.id === project.font_family)?.cssFamily ??
      BOOK_FONTS[0].cssFamily,
    weight: project.font_weight_style === 'bold' ? 700 : 400,
  };

  const interiorCount = spreads.filter((s) => s.kind === 'spread').length;
  const interiorIdx = current?.kind === 'spread' ? store.interiorIndexOf(current.id) : -1;

  const handleSplitClick = () => {
    if (unreviewedElements.length > 0) {
      setUnreviewedModalOpen(true);
      return;
    }
    setSplitDialogOpen(true);
  };

  const handleSplitStory = async () => {
    if (await splitStory()) setSplitDialogOpen(false);
  };

  const handleDeleteSpread = async () => {
    if (!current) return;
    setDeletingSpread(true);
    try {
      if (await deleteSpread(current.id)) setConfirmDeleteSpread(false);
    } finally {
      setDeletingSpread(false);
    }
  };

  const currentLabel = current ? labelFor(current) : null;
  const hasSpreads = spreads.length > 0;

  return (
    <div className="compose-stage" data-testid="compose-stage">
      {/* ── Top bar ── */}
      <div className="compose-bar">
        <span className="compose-bar__step">3 · Compose</span>
        {hasSpreads && current && (
          <div className="compose-bar__nav">
            <button
              type="button"
              className="iconbtn iconbtn--labeled"
              onClick={() => step(-1)}
              disabled={idx <= 0}
              aria-label="Previous spread"
            >
              <IconArrowLeft />
              <span>Previous</span>
            </button>
            <span className="compose-bar__title">
              <strong>{currentLabel?.label}</strong>
              {currentLabel?.pages && (
                <span className="compose-bar__pages">pages {currentLabel.pages}</span>
              )}
            </span>
            <button
              type="button"
              className="iconbtn iconbtn--labeled"
              onClick={() => step(1)}
              disabled={idx >= spreads.length - 1}
              aria-label="Next spread"
            >
              <span>Next</span>
              <IconArrowRight />
            </button>
            {current.kind === 'spread' && (
              <>
                <button
                  type="button"
                  className="iconbtn iconbtn--sm iconbtn--labeled"
                  title="Move spread earlier"
                  aria-label="Move spread earlier"
                  onClick={() => void moveSpread(current.id, -1)}
                  disabled={interiorIdx <= 0}
                >
                  <IconArrowLeft />
                  <span>Move earlier</span>
                </button>
                <button
                  type="button"
                  className="iconbtn iconbtn--sm iconbtn--labeled"
                  title="Move spread later"
                  aria-label="Move spread later"
                  onClick={() => void moveSpread(current.id, 1)}
                  disabled={interiorIdx < 0 || interiorIdx >= interiorCount - 1}
                >
                  <span>Move later</span>
                  <IconArrowRight />
                </button>
                <button
                  type="button"
                  className="iconbtn iconbtn--sm iconbtn--labeled"
                  title="Delete this spread"
                  aria-label="Delete this spread"
                  onClick={() => setConfirmDeleteSpread(true)}
                >
                  <IconTrash />
                  <span>Delete</span>
                </button>
              </>
            )}
          </div>
        )}
        <div className="compose-bar__spacer" />
        {layoutUndo && layoutUndo.spreadId === current?.id && (
          <Button variant="ghost" size="sm" onClick={undoLayout}>
            Undo layout
          </Button>
        )}
        {hasSpreads && current && current.elements.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            icon={<IconWand />}
            loading={layouting}
            onClick={() => void layoutSpread()}
            title="Let AI arrange this spread's images and text"
          >
            AI layout
          </Button>
        )}
        {hasSpreads && (
          <Button variant="ghost" size="sm" icon={<IconSparkle />} onClick={handleSplitClick}>
            Auto-split
          </Button>
        )}
        <span className="compose-bar__autosave" data-testid="compose-autosave">
          <span
            className={`compose-bar__dot${
              saveState === 'saving'
                ? ' compose-bar__dot--saving'
                : saveState === 'error'
                  ? ' compose-bar__dot--error'
                  : ''
            }`}
          />
          {saveState === 'saving' ? 'Saving…' : saveState === 'error' ? 'Save failed' : 'Autosaved'}
        </span>
        <Button
          variant="ghost"
          size="sm"
          icon={<IconMagnifier />}
          onClick={() => setPreviewOpen(true)}
          disabled={!hasSpreads}
        >
          Preview
        </Button>
        <Button
          variant="accent"
          size="sm"
          icon={<IconDownload />}
          onClick={() => void goToStage('export')}
          disabled={!hasSpreads}
        >
          Export book
        </Button>
      </div>

      {/* ── Stage ── */}
      {hasSpreads ? (
        <div className="dir-b">
          <div className="dir-b__dock">
            <DockButton
              icon={<IconPointer />}
              label="Select"
              active={tool === null}
              onClick={() => setTool(null)}
            />
            <div className="tool-sep" />
            <DockButton
              icon={<IconImage />}
              label="Images"
              active={tool === 'bank'}
              onClick={() => setTool(tool === 'bank' ? null : 'bank')}
            />
            <DockButton icon={<IconType />} label="Text" onClick={addText} />
            <DockButton
              icon={<IconLayers />}
              label="Layers"
              active={tool === 'layers'}
              onClick={() => setTool(tool === 'layers' ? null : 'layers')}
            />
          </div>

          {tool === 'bank' && (
            <FloatPanel title="Image bank" onClose={() => setTool(null)}>
              <BankPanel
                store={store}
                onEditCastElement={setCastEditorElementId}
                onRefineBankImage={(id, r2Key, caption) =>
                  setRefiningBank({ id, r2Key, caption })
                }
              />
            </FloatPanel>
          )}
          {tool === 'layers' && (
            <FloatPanel title="Layers" onClose={() => setTool(null)}>
              <LayersPanel store={store} />
            </FloatPanel>
          )}

          <div className="dir-b__stage">
            <SpreadCanvas store={store} bookFont={bookFont} />
          </div>

          <StoryRail store={store} />

          <div className="dir-b__pager">
            <button
              type="button"
              className="iconbtn iconbtn--labeled"
              onClick={() => step(-1)}
              disabled={idx <= 0}
              aria-label="Previous spread"
            >
              <IconArrowLeft />
              <span>Previous</span>
            </button>
            <div className="dir-b__pager-strip">
              <SpreadNavigator store={store} />
            </div>
            <button
              type="button"
              className="iconbtn iconbtn--labeled"
              onClick={() => step(1)}
              disabled={idx >= spreads.length - 1}
              aria-label="Next spread"
            >
              <span>Next</span>
              <IconArrowRight />
            </button>
          </div>
        </div>
      ) : (
        /* ── Empty state — first visit ── */
        <div className="compose-hero">
          <h1 className="compose-hero__title">
            Compose the <em>book</em>.
          </h1>
          <p className="compose-hero__sub">
            Lay your story out as two-page spreads: the AI finds the story&rsquo;s natural
            beats, you group them into pages on the story rail, generate art into the image
            bank, then place pictures and text exactly where you want them.
          </p>
          <div className="compose-hero__actions">
            <Button variant="accent" icon={<IconSparkle />} onClick={handleSplitClick} loading={splitting}>
              Compose from manuscript
            </Button>
            <Button variant="secondary" onClick={() => void addSpread()}>
              Start with a blank spread
            </Button>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {previewOpen && (
        <PreviewOverlay
          store={store}
          bookFont={bookFont}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      <SplitStoryDialog
        open={splitDialogOpen}
        existingSpreadCount={interiorCount}
        storyText={project.raw_story ?? ''}
        loading={splitting}
        onCancel={() => setSplitDialogOpen(false)}
        onConfirm={() => void handleSplitStory()}
      />

      <UnreviewedElementsModal
        open={unreviewedModalOpen}
        elements={unreviewedElements}
        onDismiss={() => setUnreviewedModalOpen(false)}
        onJumpToDefine={() => {
          setUnreviewedModalOpen(false);
          void goToStage('define');
        }}
        onSplitAnyway={() => {
          setUnreviewedModalOpen(false);
          setSplitDialogOpen(true);
        }}
      />

      <ConfirmDialog
        open={confirmDeleteSpread}
        title="Delete spread"
        message={
          currentLabel?.pages
            ? `Are you sure you want to delete ${currentLabel.label} (pages ${currentLabel.pages})? Placed elements are removed; the image bank keeps every generated image.`
            : 'Are you sure you want to delete this spread?'
        }
        confirmLabel="Delete"
        variant="danger"
        loading={deletingSpread}
        onConfirm={() => void handleDeleteSpread()}
        onCancel={() => setConfirmDeleteSpread(false)}
      />

      {(() => {
        const element = castEditorElementId
          ? (project.story_elements ?? []).find((e) => e.id === castEditorElementId) ?? null
          : null;
        if (!element) return null;
        return (
          <CastEditorModal
            open={true}
            element={element}
            project={project}
            updateStoryElement={updateStoryElement}
            refetch={refetch}
            onClose={() => setCastEditorElementId(null)}
          />
        );
      })()}

      {refiningBank && (
        <RefineImageModal
          open={true}
          project={project}
          imageRef={{
            kind: 'bank_image',
            project_id: project.id,
            bank_image_id: refiningBank.id,
          }}
          currentR2Key={refiningBank.r2Key}
          targetLabel={refiningBank.caption}
          onClose={() => setRefiningBank(null)}
          onAccepted={() => {
            void refetch();
          }}
        />
      )}
    </div>
  );
}

function DockButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`tool ${active ? 'tool--active' : ''}`}
      onClick={onClick}
      title={label}
    >
      {icon}
      <span className="tool__label">{label}</span>
    </button>
  );
}

function FloatPanel({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Clicking anywhere outside the panel dismisses it. Two carve-outs:
  // the dock (its buttons already toggle the panel — closing on
  // pointerdown would make the toggle reopen it), and portaled overlays
  // (modals, galleries, crop) which render into document.body, outside
  // the compose stage root.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      const panel = panelRef.current;
      if (!target || !panel || panel.contains(target)) return;
      if (target.closest('.dir-b__dock')) return;
      const stage = panel.closest('.compose-stage');
      if (stage && !stage.contains(target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [onClose]);

  return (
    <div className="float-panel" ref={panelRef}>
      <div className="float-panel__bar">
        <span className="float-panel__title">{title}</span>
        <button
          type="button"
          className="iconbtn iconbtn--sm iconbtn--labeled"
          onClick={onClose}
          aria-label="Close panel"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ci" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          <span>Close</span>
        </button>
      </div>
      <div className="float-panel__body">{children}</div>
    </div>
  );
}
